import { useId } from 'react';

/**
 * Multi-Select für Wochentage als Toggle-Button-Gruppe.
 *
 * Konvention: 0 = Montag, 6 = Sonntag (ISO-naher Wochenanfang).
 *   passt zu `MaintenanceWindowSpec.weekdays` aus der OpenAPI-Spec.
 *
 * - Buttons mit aria-pressed; role="group" auf Container
 * - Keyboard via natives Tab + Space/Enter (kein Custom-Roving nötig,
 *   da jeder Button regulär fokussierbar ist)
 */

const LABELS_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'] as const;
const LABELS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

interface WeekdayToggleProps {
  label: string;
  /** Selektierte Indizes (0..6). */
  value: number[];
  onChange: (next: number[]) => void;
  /** Optionaler Hint unterhalb. */
  hint?: string;
  disabled?: boolean;
  className?: string;
}

export function WeekdayToggle({
  label,
  value,
  onChange,
  hint,
  disabled = false,
  className,
}: WeekdayToggleProps): JSX.Element {
  const idPrefix = useId();
  const groupId = `${idPrefix}-group`;
  const hintId = hint ? `${idPrefix}-hint` : undefined;

  const toggle = (idx: number): void => {
    if (disabled) return;
    const set = new Set(value);
    if (set.has(idx)) set.delete(idx);
    else set.add(idx);
    onChange(Array.from(set).sort((a, b) => a - b));
  };

  return (
    <div className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      <span id={groupId} className="text-sm font-medium text-text">
        {label}
      </span>
      <div
        role="group"
        aria-labelledby={groupId}
        aria-describedby={hintId}
        className="flex flex-wrap gap-1"
      >
        {LABELS_SHORT.map((short, idx) => {
          const active = value.includes(idx);
          return (
            <button
              key={idx}
              type="button"
              aria-pressed={active}
              aria-label={LABELS_LONG[idx]}
              disabled={disabled}
              onClick={() => toggle(idx)}
              className={[
                'inline-flex h-9 w-12 items-center justify-center rounded-md border text-sm transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                active
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-border bg-surface text-muted hover:text-text hover:bg-surface-2',
                disabled ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              {short}
            </button>
          );
        })}
      </div>
      {hint && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
